import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Car,
  CarFront,
  Check,
  CheckCircle2,
  Cog,
  Copy,
  FileJson,
  FileSpreadsheet,
  FileText,
  Info,
  ListCheck,
  Loader,
  Loader2,
  MapPin,
  Phone,
  Printer,
  Settings,
  ShieldCheck,
  User,
} from "lucide-react";
import * as XLSX from "xlsx";
import { isAlphanumeric } from "validator";
import { useService } from "../../../../context/ServiceContext";
import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import { fetchProfile } from "../../../../Services/profile/profileservice";
import Seo from "../../../../Components/SeoComponent/Seo";
import Header from "../../../../Components/Header";
import { getRCPremiumV3 } from "../../../../Services/ApiTestService/apiTestService";

function RcPremiumV3() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [vehicleNo, setVehicleNo] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;
    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied JSON!"));

    setIsJsonCopied(true);
    setTimeout(() => setIsJsonCopied(false), 2000);
  };

  const handleRcPremiumv3 = async () => {
    if (!profileData) return;

    const validateVehicleNum = isAlpha(vehicleNo);

    if (!vehicleNo) {
      return setInputError("Vehicle Number is Required");
    }

    if (vehicleNo && !validateVehicleNum) {
      return setInputError(
        "⚠️ Please enter alpha numeric in Vehicle No. field",
      );
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getRCPremiumV3({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_number: vehicleNo.trim()?.toUpperCase(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      {/* Hide Header during print */}
      <div className="print:hidden">
        <Header heading={serviceName} />
      </div>

      {isLoading && (
        <div className="flex justify-center mt-6 print:hidden">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14 print:p-0 print:m-0">
        {/* ---------------- FORM (Hidden on print) ---------------- */}
        <div className="max-w-4xl w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100 print:hidden">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <CarFront size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {serviceName}
              </h2>
              <p className="text-sm text-slate-500">
                Verify vehicle details using Vehicle number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6 gap-6 flex justify-center items-center">
            {/* Vehicle Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Cog size={17} className="text-blue-600" />
                Vehicle Number
              </label>

              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                placeholder="Eg: GJ0***1256"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Button */}
            <div className="mt-8 flex ">
              <button
                disabled={isLoading || !vehicleNo}
                onClick={handleRcPremiumv3}
                className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Vehicle
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}
        </div>

        {/* response message (Hidden on print) */}
        {response && (
          <div
            className={` ${
              response.status?.code === 500 || response.status?.code === 422
                ? "text-red-500"
                : "text-green-600"
            } py-4 text-2xl font-bold print:hidden`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions (Hidden on print) */}
        <div className="w-full flex justify-center print:hidden">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Vehicle Number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl print:block print:w-full print:max-w-none print:mt-0">
            {/* -------- JSON VIEW (Hidden on print) -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto print:hidden">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8 flex items-center gap-1 bg-gray-800 hover:bg-gray-700 px-3 py-1 rounded text-xs"
              >
                {isJsonCopied ? <Check size={14} /> : <Copy size={14} />}
                {isJsonCopied ? "Copied" : "Copy JSON"}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-2" />
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            <div className="bg-gray-200 p-2 rounded-md print:bg-white print:p-0">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1 print:hidden">
                <ListCheck />
                RC Details
              </h2>

              {response?.status?.code === 200 && (
                <RCDetailsDashboard response={response} />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default RcPremiumV3;

const RCDetailsDashboard = ({ response }) => {
  const [isFormattedCopied, setIsFormattedCopied] = useState(false);

  // Safe exit if data is missing or status is invalid
  if (response?.status?.code !== 200 || !response?.data?.rc_details) {
    return null;
  }

  // Target the nested 'rc_details' object and registered_mobile
  const data = response.data.rc_details;
  const registeredMobile = response.data.registered_mobile?.mobile;

  // Helper to safely parse dates in "DD-MMM-YYYY" format
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return new Date(`${parts[1]} ${parts[0]}, ${parts[2]}`);
    }
    return new Date(dateStr);
  };

  // Helper to check if a document is expired
  const isExpired = (dateString) => {
    const expiry = parseDate(dateString);
    if (!expiry || isNaN(expiry.getTime())) return false;
    return expiry < new Date();
  };

  // Safe lower string converter
  const safeLower = (str) => (str ? str.toLowerCase() : "");

  // 1. COPY FORMATTED DATA FUNCTION
  const handleCopyFormattedText = () => {
    const formattedText = `
==================================================
              RC VEHICLE DETAILS (V3)
==================================================
Vehicle Reg No : ${data.regNo || "N/A"}
Status         : ${data.status || "N/A"}
Owner Name     : ${data.owner || "N/A"}
Owner Count    : ${data.ownerCount || "N/A"}
Father Name    : ${data.ownerFatherName || "N/A"}
Mobile         : ${registeredMobile || "N/A"}

--------------------------------------------------
REGISTRATION DETAILS
--------------------------------------------------
Authority (RTO)   : ${data.regAuthority || "N/A"}
RTO Code          : ${data.rtoCode || "N/A"}
Vehicle Class     : ${data.vehicleClass || "N/A"}
Registration Date : ${data.regDate || "N/A"}
RC Expiry Date    : ${data.rcExpiryDate || "N/A"}
Address           : ${data.presentAddress || "N/A"}

--------------------------------------------------
VEHICLE SPECIFICATIONS
--------------------------------------------------
Manufacturer     : ${data.vehicleManufacturerName || "N/A"}
Model            : ${data.model || "N/A"}
Fuel Type        : ${data.type || "N/A"}
Chassis Number   : ${data.chassis || "N/A"}
Engine Number    : ${data.engine || "N/A"}
Color            : ${data.vehicleColour || "N/A"}
Body Type        : ${data.bodyType || "N/A"}
Emission Norms   : ${data.normsType || "N/A"}
Engine CC        : ${data.vehicleCubicCapacity ? `${data.vehicleCubicCapacity} cc` : "N/A"}

--------------------------------------------------
COMPLIANCE & INSURANCE
--------------------------------------------------
Insurance Company  : ${data.vehicleInsuranceCompanyName || "N/A"}
Policy Number      : ${data.vehicleInsurancePolicyNumber || "N/A"}
Insurance Valid    : ${data.vehicleInsuranceUpto || "N/A"}
PUCC Number        : ${data.puccNumber || "N/A"}
PUCC Valid Upto    : ${data.puccUpto || "N/A"}
Road Tax Paid Upto : ${data.vehicleTaxUpto || "N/A"}

--------------------------------------------------
FINANCE & WEIGHT
--------------------------------------------------
Financer Status    : ${data.financed ? "Yes" : "No"}
Financer           : ${data.rcFinancer || "None (Cash/Fully Paid)"}
Commercial Vehicle : ${data.isCommercial ? "Yes" : "No"}
Permit Type        : ${data.permitType || "Not Applicable"}
Permit Number      : ${data.permitNumber || "N/A"}
Permit Valid Upto  : ${data.permitValidUpto || "N/A"}
Gross Weight       : ${data.grossVehicleWeight ? `${data.grossVehicleWeight} kg` : "N/A"}
Unladen Weight     : ${data.unladenWeight ? `${data.unladenWeight} kg` : "N/A"}
Seating Capacity   : ${data.vehicleSeatCapacity || "N/A"}
==================================================
    `.trim();

    navigator.clipboard.writeText(formattedText).then(() => {
      showSuccessToast("Formatted RC data copied!");
      setIsFormattedCopied(true);
      setTimeout(() => setIsFormattedCopied(false), 2000);
    });
  };

  // 2. EXPORT TO EXCEL FUNCTION (Field & Value ONLY - No Category column)
  const handleExportToExcel = () => {
    const formattedRows = [
      { Field: "Registration No", Value: data.regNo || "N/A" },
      { Field: "Status", Value: data.status || "N/A" },
      { Field: "Owner Name", Value: data.owner || "N/A" },
      { Field: "Owner Count", Value: data.ownerCount || "N/A" },
      { Field: "Father's Name", Value: data.ownerFatherName || "N/A" },
      { Field: "Registered Mobile", Value: registeredMobile || "N/A" },
      { Field: "Authority (RTO)", Value: data.regAuthority || "N/A" },
      { Field: "RTO Code", Value: data.rtoCode || "N/A" },
      { Field: "Vehicle Class", Value: data.vehicleClass || "N/A" },
      { Field: "Registration Date", Value: data.regDate || "N/A" },
      { Field: "RC Expiry Date", Value: data.rcExpiryDate || "N/A" },
      { Field: "Present Address", Value: data.presentAddress || "N/A" },
      { Field: "Manufacturer", Value: data.vehicleManufacturerName || "N/A" },
      { Field: "Model", Value: data.model || "N/A" },
      { Field: "Fuel Type", Value: data.type || "N/A" },
      { Field: "Chassis Number", Value: data.chassis || "N/A" },
      { Field: "Engine Number", Value: data.engine || "N/A" },
      { Field: "Color", Value: data.vehicleColour || "N/A" },
      { Field: "Body Type", Value: data.bodyType || "N/A" },
      { Field: "Emission Norms", Value: data.normsType || "N/A" },
      {
        Field: "Engine CC",
        Value: data.vehicleCubicCapacity
          ? `${data.vehicleCubicCapacity} cc`
          : "N/A",
      },
      {
        Field: "Insurance Company",
        Value: data.vehicleInsuranceCompanyName || "N/A",
      },
      {
        Field: "Insurance Policy No",
        Value: data.vehicleInsurancePolicyNumber || "N/A",
      },
      {
        Field: "Insurance Valid Upto",
        Value: data.vehicleInsuranceUpto || "N/A",
      },
      { Field: "PUCC Number", Value: data.puccNumber || "N/A" },
      { Field: "PUCC Valid Upto", Value: data.puccUpto || "N/A" },
      { Field: "Road Tax Paid Upto", Value: data.vehicleTaxUpto || "N/A" },
      { Field: "Financed Status", Value: data.financed ? "Yes" : "No" },
      { Field: "Financer Name", Value: data.rcFinancer || "None" },
      { Field: "Commercial Vehicle", Value: data.isCommercial ? "Yes" : "No" },
      { Field: "Permit Type", Value: data.permitType || "N/A" },
      { Field: "Permit Number", Value: data.permitNumber || "N/A" },
      { Field: "Permit Valid Upto", Value: data.permitValidUpto || "N/A" },
      {
        Field: "Gross Weight",
        Value: data.grossVehicleWeight
          ? `${data.grossVehicleWeight} kg`
          : "N/A",
      },
      {
        Field: "Unladen Weight",
        Value: data.unladenWeight ? `${data.unladenWeight} kg` : "N/A",
      },
      { Field: "Seating Capacity", Value: data.vehicleSeatCapacity || "N/A" },
    ];

    const worksheet = XLSX.utils.json_to_sheet(formattedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RC Details");

    // Set column widths
    worksheet["!cols"] = [{ wch: 30 }, { wch: 50 }];

    XLSX.writeFile(workbook, `RC_Details_${data.regNo || "Vehicle"}.xlsx`);
    showSuccessToast("Excel file downloaded successfully!");
  };

  // 3. PRINT FUNCTION
  const handlePrint = () => {
    window.print();
  };

  // Reusable component for small data blocks
  const InfoBlock = ({ label, value, highlight = false, hasError = false }) => (
    <div className="flex flex-col">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <span
          className={`text-sm ${
            hasError
              ? "font-bold text-red-600"
              : highlight
                ? "font-bold text-gray-900"
                : "font-semibold text-gray-800"
          }`}
        >
          {value || "N/A"}
        </span>
        {hasError && <AlertTriangle size={14} className="text-red-500" />}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6 font-sans">
      {/* Action Buttons Bar (Export to Excel, Print, Copy) */}
      <div className="flex flex-wrap justify-end items-center gap-3 mb-4 print:hidden">
        <button
          onClick={handleCopyFormattedText}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-slate-700 hover:bg-slate-50 border border-slate-300 font-semibold text-xs rounded-xl shadow-sm transition"
        >
          {isFormattedCopied ? (
            <Check size={15} className="text-green-600" />
          ) : (
            <Copy size={15} />
          )}
          {isFormattedCopied ? "Copied Data" : "Copy Formatted"}
        </button>

        <button
          onClick={handleExportToExcel}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-xl shadow-sm transition"
        >
          <FileSpreadsheet size={15} />
          Export to Excel
        </button>

        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-xl shadow-sm transition"
        >
          <Printer size={15} />
          Print
        </button>
      </div>

      {/* 1. Primary Hero Banner */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden print:shadow-none print:border">
        <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-100 flex flex-col items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl shadow-inner print:hidden">
              <Car size={32} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                  {data.regNo}
                </h2>
                <span
                  className={`px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1.5 uppercase tracking-wide ${
                    data.status === "ACTIVE"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {data.status === "ACTIVE" ? (
                    <CheckCircle2 size={14} />
                  ) : (
                    <AlertCircle size={14} />
                  )}
                  {data.status}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-600 mt-1 capitalize">
                {safeLower(data.vehicleManufacturerName)} •{" "}
                {safeLower(data.model)} ({data.type})
              </p>
            </div>
          </div>

          <div className="text-left border-t md:border-t-0 pt-4 md:pt-0">
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">
              Registered Owner (
              {data.ownerCount > 1 ? `${data.ownerCount}nd Owner` : "1st Owner"}
              )
            </p>
            <p className="text-lg font-bold text-gray-900 capitalize">
              {safeLower(data.owner)}
            </p>
            {data.ownerFatherName && (
              <p className="text-xs text-gray-500 capitalize">
                C/O: {safeLower(data.ownerFatherName)}
              </p>
            )}
            {registeredMobile && (
              <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                <Phone size={12} /> {registeredMobile}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 2. Detailed Data Grid */}
      <div className="grid grid-cols-1 gap-6">
        {/* Registration & RTO Details */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 print:shadow-none print:border print:p-4">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <MapPin size={18} className="text-blue-500 print:hidden" />
            <h3 className="text-base font-bold text-gray-800">
              Registration Details
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            <InfoBlock label="Authority (RTO)" value={data.regAuthority} />
            <InfoBlock label="Vehicle Class" value={data.vehicleClass} />
            <InfoBlock label="Registration Date" value={data.regDate} />
            <InfoBlock
              label="RC Expiry"
              value={data.rcExpiryDate}
              highlight
              hasError={isExpired(data.rcExpiryDate)}
            />
            <div className="col-span-2">
              <InfoBlock label="Address" value={data.presentAddress} />
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 print:shadow-none print:border print:p-4">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <Settings size={18} className="text-gray-500 print:hidden" />
            <h3 className="text-base font-bold text-gray-800">
              Vehicle Specifications
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-4">
            <div className="col-span-2">
              <InfoBlock label="Chassis Number" value={data.chassis} />
            </div>
            <div className="col-span-2">
              <InfoBlock label="Engine Number" value={data.engine} />
            </div>
            <InfoBlock label="Color" value={data.vehicleColour} />
            <InfoBlock label="Body Type" value={data.bodyType} />
            <InfoBlock label="Norms" value={data.normsType} />
            <InfoBlock
              label="Engine Cap (CC)"
              value={
                data.vehicleCubicCapacity
                  ? `${data.vehicleCubicCapacity} cc`
                  : null
              }
            />
          </div>
        </div>

        {/* Compliance & Insurance */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 print:shadow-none print:border print:p-4">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <ShieldCheck size={18} className="text-green-500 print:hidden" />
            <h3 className="text-base font-bold text-gray-800">
              Compliance & Insurance
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-y-5 gap-x-6">
            <div>
              <InfoBlock
                label="Insurance Company"
                value={data.vehicleInsuranceCompanyName}
              />
              <p className="text-xs text-gray-500 mt-1 font-mono">
                {data.vehicleInsurancePolicyNumber || "No Policy Number"}
              </p>
            </div>
            <InfoBlock
              label="Insurance Valid Upto"
              value={data.vehicleInsuranceUpto}
              highlight
              hasError={isExpired(data.vehicleInsuranceUpto)}
            />

            <InfoBlock label="PUCC Number" value={data.puccNumber} />
            <InfoBlock
              label="PUCC Valid Upto"
              value={data.puccUpto}
              highlight
              hasError={isExpired(data.puccUpto)}
            />

            <InfoBlock
              label="Road Tax Paid Upto"
              value={data.vehicleTaxUpto}
              highlight
              hasError={isExpired(data.vehicleTaxUpto)}
            />
          </div>
        </div>

        {/* Finance & Weight */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 print:shadow-none print:border print:p-4">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <FileText size={18} className="text-purple-500 print:hidden" />
            <h3 className="text-base font-bold text-gray-800">
              Finance & Weight
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            <div className="col-span-2">
              <InfoBlock
                label="Financer"
                value={data.rcFinancer || "None (Cash/Fully Paid)"}
              />
            </div>

            <InfoBlock
              label="Commercial Vehicle"
              value={data.isCommercial ? "Yes" : "No"}
            />
            <InfoBlock
              label="Permit Details"
              value={data.permitType || "Not Applicable"}
            />

            <InfoBlock
              label="Gross Weight"
              value={
                data.grossVehicleWeight ? `${data.grossVehicleWeight} kg` : null
              }
            />
            <InfoBlock
              label="Unladen Weight"
              value={data.unladenWeight ? `${data.unladenWeight} kg` : null}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
